import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus, 
  Edit2, 
  Save,
  X,
  Printer, 
  Download,
  ChevronDown,
  ChevronUp,
  Check,
  AlertCircle,
  ArrowUpDown,
  Link2
} from 'lucide-react';
import { format } from 'date-fns';
import {
  getPattern,
  getPatternRevisions,
  createPatternRevision,
  updatePatternRevision,
  getMeasurements,
  getGarmentSpecificationFields,
  getGarmentTypeConfigs,
  getCustomer
} from '@/lib/api';

const PatternDetailPage = () => {
  const { patternId } = useParams();
  const navigate = useNavigate();

  const [pattern, setPattern] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [currentRevision, setCurrentRevision] = useState(null);
  const [bodyMeasurements, setBodyMeasurements] = useState(null);
  const [specFields, setSpecFields] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI State
  const [compareMode, setCompareMode] = useState(false);
  const [selectedRevisionIds, setSelectedRevisionIds] = useState([]);
  const [newRevisionDialogOpen, setNewRevisionDialogOpen] = useState(false);
  const [editingRevisionId, setEditingRevisionId] = useState(null);
  const [editedSpecs, setEditedSpecs] = useState({});
  const [saving, setSaving] = useState(false);

  // Form state for new revision
  const [revisionForm, setRevisionForm] = useState({
    revision_notes: '',
    garment_specifications: {},
    copyFromRevisionId: null,
  });

  useEffect(() => {
    fetchPatternData();
  }, [patternId]);

  const fetchPatternData = async () => {
    try {
      setLoading(true);
      
      // Fetch pattern details
      const patternRes = await getPattern(patternId);
      const patternData = patternRes.data;
      setPattern(patternData);

      // Fetch customer details
      const customerRes = await getCustomer(patternData.customer_id);
      setCustomer(customerRes.data);

      // Fetch pattern revisions
      const revisionsRes = await getPatternRevisions(patternId);
      const sortedRevisions = revisionsRes.data.sort((a, b) => 
        b.revision_number - a.revision_number
      );
      setRevisions(sortedRevisions);

      // Set current revision (most recent with in-use status or first one)
      const activeRevision = sortedRevisions.find(r => r.status === 'in-use') || sortedRevisions[0];
      setCurrentRevision(activeRevision);

      // Initialize selected revisions for comparison
      if (sortedRevisions.length >= 2) {
        setSelectedRevisionIds([sortedRevisions[0].revision_id, sortedRevisions[1].revision_id]);
      } else if (sortedRevisions.length === 1) {
        setSelectedRevisionIds([sortedRevisions[0].revision_id]);
      }

      // Fetch body measurements (get latest)
      const measurementsRes = await getMeasurements(patternData.customer_id);
      if (measurementsRes.data && measurementsRes.data.length > 0) {
        // If active revision has linked measurement, use that, otherwise use latest
        if (activeRevision?.linked_measurement_id) {
          const bodyMeas = measurementsRes.data.find(
            m => m.measurement_id === activeRevision.linked_measurement_id
          );
          setBodyMeasurements(bodyMeas || measurementsRes.data[0]);
        } else {
          setBodyMeasurements(measurementsRes.data[0]);
        }
      }

      // Fetch garment type configs to get config_id
      const configsRes = await getGarmentTypeConfigs();
      const config = configsRes.data.find(c => c.garment_type === patternData.garment_type);
      
      if (config) {
        // Fetch specification fields for this garment type config
        const fieldsRes = await getGarmentSpecificationFields(config.config_id);
        // Filter only number type fields for the measurement table
        const numberFields = fieldsRes.data.filter(f => f.field_type === 'number').sort((a, b) => a.display_order - b.display_order);
        setSpecFields(numberFields);
      }

    } catch (error) {
      console.error('Failed to fetch pattern data:', error);
      toast.error('Failed to load pattern');
    } finally {
      setLoading(false);
    }
  };

  const handleNewRevision = () => {
    // Allow creating first revision even when no current revision exists
    setRevisionForm({
      revision_notes: '',
      garment_specifications: currentRevision ? { ...currentRevision.garment_specifications } : {},
      copyFromRevisionId: currentRevision?.revision_id || null,
    });
    setNewRevisionDialogOpen(true);
  };

  const handleSaveNewRevision = async () => {
    try {
      setSaving(true);
      await createPatternRevision({
        pattern_id: patternId,
        revision_notes: revisionForm.revision_notes,
        garment_specifications: revisionForm.garment_specifications,
        linked_measurement_id: bodyMeasurements?.measurement_id || null,
        status: 'draft',
      });
      
      toast.success('New revision created');
      setNewRevisionDialogOpen(false);
      fetchPatternData();
    } catch (error) {
      toast.error('Failed to create revision');
    } finally {
      setSaving(false);
    }
  };

  const handleEditRevision = (revision) => {
    setEditingRevisionId(revision.revision_id);
    setEditedSpecs({ ...revision.garment_specifications });
  };

  const handleCancelEdit = () => {
    setEditingRevisionId(null);
    setEditedSpecs({});
  };

  const handleSaveEdit = async () => {
    if (!editingRevisionId) return;
    
    try {
      setSaving(true);
      const revision = revisions.find(r => r.revision_id === editingRevisionId);
      
      await updatePatternRevision(editingRevisionId, {
        pattern_id: revision.pattern_id,
        revision_notes: revision.revision_notes,
        linked_measurement_id: revision.linked_measurement_id,
        garment_specifications: editedSpecs,
        status: revision.status,
      });
      
      toast.success('Measurements updated');
      setEditingRevisionId(null);
      setEditedSpecs({});
      fetchPatternData();
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSpecChange = (fieldKey, value) => {
    setEditedSpecs(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const toggleCompareMode = () => {
    setCompareMode(!compareMode);
  };

  const toggleRevisionSelection = (revisionId) => {
    if (selectedRevisionIds.includes(revisionId)) {
      setSelectedRevisionIds(selectedRevisionIds.filter(id => id !== revisionId));
    } else {
      setSelectedRevisionIds([...selectedRevisionIds, revisionId]);
    }
  };

  const getRevisionById = (revisionId) => {
    return revisions.find(r => r.revision_id === revisionId);
  };

  const getValueDiff = (currentVal, prevVal) => {
    if (!prevVal || !currentVal) return null;
    const diff = parseFloat(currentVal) - parseFloat(prevVal);
    if (isNaN(diff) || diff === 0) return null;
    return diff > 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2);
  };

  const renderMeasurementTable = () => {
    if (!currentRevision || !specFields.length) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No measurement specifications available</p>
        </div>
      );
    }

    const displayRevisions = compareMode 
      ? selectedRevisionIds.map(id => getRevisionById(id)).filter(Boolean)
      : [currentRevision];

    const unit = bodyMeasurements?.unit || 'in';

    return (
      <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider w-64 sticky left-0 bg-muted/50">
                  Specification
                </th>
                {bodyMeasurements && (
                  <th className="px-4 py-3 text-center font-semibold text-xs uppercase tracking-wider w-40 bg-blue-50/80">
                    <div className="flex flex-col items-center gap-1">
                      <span>Body</span>
                      <span className="text-[10px] font-normal text-muted-foreground">
                        {format(new Date(bodyMeasurements.measurement_date), 'MMM d, yyyy')}
                      </span>
                    </div>
                  </th>
                )}
                {displayRevisions.map((rev, idx) => {
                  const isEditing = editingRevisionId === rev.revision_id;
                  return (
                    <th 
                      key={rev.revision_id} 
                      className={`px-4 py-3 text-center font-semibold text-xs uppercase tracking-wider min-w-[180px] ${
                        idx === 0 ? 'bg-green-50/80' : 'bg-gray-50/80'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold">
                            V{rev.revision_number}
                          </span>
                          {rev.status === 'in-use' && (
                            <Badge variant="outline" className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">
                              Active
                            </Badge>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground font-normal">
                          {format(new Date(rev.created_at), 'MMM d, yyyy')}
                        </div>
                        {!compareMode && !isEditing && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 text-xs mt-1"
                            onClick={() => handleEditRevision(rev)}
                          >
                            <Edit2 className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y">
              {specFields.map((field, fieldIdx) => {
                const fieldKey = field.field_key;
                const bodyValue = bodyMeasurements?.[field.linked_measurement_field];
                
                return (
                  <tr key={field.field_id} className={fieldIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}>
                    <td className="px-4 py-3 sticky left-0 bg-inherit">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{field.field_label}</span>
                        {field.linked_measurement_field && (
                          <Link2 className="w-3 h-3 text-amber-500" title="Linked to body measurement" />
                        )}
                      </div>
                    </td>
                    
                    {bodyMeasurements && (
                      <td className="px-4 py-3 text-center bg-blue-50/30">
                        <span className="font-mono text-sm text-muted-foreground">
                          {bodyValue ? `${bodyValue} ${unit}` : '—'}
                        </span>
                      </td>
                    )}
                    
                    {displayRevisions.map((rev, revIdx) => {
                      const isEditing = editingRevisionId === rev.revision_id;
                      const value = isEditing 
                        ? editedSpecs[fieldKey] 
                        : rev.garment_specifications?.[fieldKey];
                      
                      const prevRev = revIdx < displayRevisions.length - 1 
                        ? displayRevisions[revIdx + 1] 
                        : null;
                      const prevValue = prevRev?.garment_specifications?.[fieldKey];
                      const diff = getValueDiff(value, prevValue);
                      
                      return (
                        <td 
                          key={rev.revision_id} 
                          className={`px-4 py-3 text-center ${
                            revIdx === 0 ? 'bg-green-50/30' : 'bg-gray-50/30'
                          }`}
                        >
                          {isEditing ? (
                            <Input
                              type="number"
                              step="0.25"
                              className="w-28 mx-auto text-center h-8"
                              value={value || ''}
                              onChange={(e) => handleSpecChange(fieldKey, e.target.value)}
                              placeholder="—"
                            />
                          ) : (
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="font-mono font-medium">
                                {value ? `${value} ${unit}` : '—'}
                              </span>
                              {diff && compareMode && (
                                <span className={`text-xs font-medium ${
                                  diff.startsWith('+') ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {diff.startsWith('+') ? '↑' : '↓'} {diff}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Save/Cancel buttons for edit mode */}
        {editingRevisionId && (
          <div className="border-t bg-muted/50 px-6 py-3 flex items-center justify-end gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleCancelEdit}
              disabled={saving}
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button 
              size="sm"
              onClick={handleSaveEdit}
              disabled={saving}
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col">
        <div className="border-b bg-card shadow-sm p-6">
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="flex-1 p-6 space-y-4">
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!pattern) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-lg font-medium">Pattern not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background p-3">
      {/* Fixed Header */}
      <div className="border-b bg-card shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back button and Pattern Info */}
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(-1)}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              
              <div className="h-8 w-px bg-border" />
              
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-semibold">
                    {pattern.pattern_code}
                  </h1>
                  <Badge variant="outline" className="bg-blue-100 text-blue-700 capitalize">
                    {pattern.garment_type}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Customer: {customer?.name || 'Loading...'}</span>
                  <span>•</span>
                  <span>{revisions.length} revision{revisions.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              {revisions.length > 1 && (
                <Button
                  variant={compareMode ? "default" : "outline"}
                  size="sm"
                  onClick={toggleCompareMode}
                  className="gap-2"
                  disabled={editingRevisionId !== null}
                >
                  <ArrowUpDown className="w-4 h-4" />
                  {compareMode ? 'Exit Compare' : 'Compare Revisions'}
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleNewRevision}
                className="gap-2"
                disabled={editingRevisionId !== null}
              >
                <Plus className="w-4 h-4" />
                New Revision
              </Button>

              <div className="h-6 w-px bg-border" />

              <Button
                variant="ghost"
                size="sm"
                className="gap-2"
                disabled
              >
                <Printer className="w-4 h-4" />
                Print
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="gap-2"
                disabled
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
            </div>
          </div>

          {/* Compare Mode: Revision Selector */}
          {compareMode && revisions.length > 1 && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium">Select revisions to compare:</span>
                <Badge variant="secondary">{selectedRevisionIds.length} selected</Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                {revisions.map((rev) => {
                  const isSelected = selectedRevisionIds.includes(rev.revision_id);
                  return (
                    <Button
                      key={rev.revision_id}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleRevisionSelection(rev.revision_id)}
                      className="gap-2"
                    >
                      {isSelected && <Check className="w-3 h-3" />}
                      V{rev.revision_number}
                      {rev.status === 'in-use' && ' (Active)'}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <ScrollArea className="flex-1">
        <div className="container mx-auto px-6 py-6">
          {/* Info Banner */}
          {bodyMeasurements && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600" />
              <p className="text-sm text-blue-900">
                Body measurements from <strong>{format(new Date(bodyMeasurements.measurement_date), 'MMMM d, yyyy')}</strong> are shown for reference
              </p>
            </div>
          )}

          {/* Measurement Table */}
          <div className="mb-6">
            {renderMeasurementTable()}
          </div>

          {/* Revision Notes */}
          {currentRevision?.revision_notes && !compareMode && (
            <Card>
              <CardContent className="p-4">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Revision Notes</Label>
                <p className="mt-2 text-sm">{currentRevision.revision_notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>

      {/* New Revision Dialog */}
      <Dialog open={newRevisionDialogOpen} onOpenChange={setNewRevisionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Revision</DialogTitle>
            <DialogDescription>
              {currentRevision 
                ? "Create a new version of this pattern based on the current measurements"
                : "Create the first revision for this pattern"
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {currentRevision ? (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <p className="text-sm text-blue-900">
                  This will duplicate V{currentRevision.revision_number}'s measurements. You can edit them immediately after creation.
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-900">
                  This will be the first revision (V1) for this pattern. You can add measurements after creation.
                </p>
              </div>
            )}
            <div>
              <Label>Revision Notes (Optional)</Label>
              <Textarea
                placeholder="Describe what changes are being made..."
                value={revisionForm.revision_notes}
                onChange={(e) => setRevisionForm({ ...revisionForm, revision_notes: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setNewRevisionDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveNewRevision}
              disabled={saving}
            >
              {saving ? 'Creating...' : 'Create Revision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatternDetailPage;
